import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useLocalStorage } from "react-use";
import { useApi } from "./ApiContainer";
import { useIdentity } from "./IdentityContainer";
import SafeIframe from "../components/SafeIframe";

interface DatabaseAnnouncement {
  id: string;
  title: string;
  body: string;
  video_url?: string;
  image_url?: string;
  action_link?: string;
  action_link_text?: string;
  expiration_date: string;
  created_at: string;
  updated_at: string;
}

type SeenAnnouncements = {
  [key: string]: boolean;
};

interface AnnouncementContentProps {
  announcement: DatabaseAnnouncement;
}

const AnnouncementContent: React.FC<AnnouncementContentProps> = ({ announcement }) => (
  <Stack direction="column" spacing={2}>
    <Divider />
    <Typography sx={{ whiteSpace: 'pre-line' }}>{announcement.body}</Typography>

    {announcement.action_link && (
      <Button
        variant="contained"
        href={announcement.action_link}
        target="_blank"
        rel="noopener noreferrer"
        fullWidth
        sx={{
          backgroundColor: '#1976d2',
          '&:hover': {
            backgroundColor: '#1565c0',
          },
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)',
            },
            '50%': {
              transform: 'scale(1.02)',
              boxShadow: '0 0 20px 0 rgba(25, 118, 210, 0.4)',
            },
          },
        }}
      >
        {announcement.action_link_text || 'Learn More'}
      </Button>
    )}

    {announcement.video_url && (
      <SafeIframe iframeHtml={announcement.video_url} />
    )}

    {announcement.image_url && (
      <Box sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        my: 1
      }}>
        <img
          src={announcement.image_url}
          alt={announcement.title}
          style={{
            width: '100%',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: '8px'
          }}
        />
      </Box>
    )}
  </Stack>
);

export const AnnouncementContainer: React.FC<{
  enabled?: boolean;
  children?: React.ReactNode;
}> = ({ enabled = true, children }) => {
  const { postgrest } = useApi();
  const { isLoggedIn } = useIdentity();
  const [activeAnnouncement, setActiveAnnouncement] = React.useState<DatabaseAnnouncement | null>(null);
  const [seenAnnouncements, setSeenAnnouncements] = useLocalStorage<SeenAnnouncements>(
    "airplx/seen-announcements",
    {},
    {
      raw: false,
      serializer: (value: SeenAnnouncements) => JSON.stringify(value),
      deserializer: (value: string) => JSON.parse(value) as SeenAnnouncements,
    }
  );

  React.useEffect(() => {
    if (!enabled || !isLoggedIn || !postgrest) return;

    const fetchAnnouncements = async () => {
      try {
        const { data, error } = await postgrest
          .rpc("get_active_announcements");
        
        if (error) {
          // Don't log authentication errors or 404s as they're expected during initial load
          if (error.code !== 'PGRST301' && !error.message?.includes('JWT')) {
            console.warn("Announcements API error:", error.message || error);
          }
          return;
        }
        
        // Since we now only get the most recent active announcement
        const activeAnn = data && data.length > 0 ? data[0] : null;

        // Show the active announcement if it hasn't been seen
        if (activeAnn && !seenAnnouncements[activeAnn.id]) {
          setActiveAnnouncement(activeAnn);
        }
      } catch (error) {
        // Only log unexpected errors
        if (error instanceof Error && !error.message?.includes('JWT') && !error.message?.includes('401')) {
          console.warn("Unexpected error fetching announcements:", error.message);
        }
      }
    };

    fetchAnnouncements();
  }, [enabled, isLoggedIn, postgrest]);

  const handleClose = () => {
    if (activeAnnouncement) {
      setSeenAnnouncements({
        ...seenAnnouncements,
        [activeAnnouncement.id]: true,
      });
      setActiveAnnouncement(null);
    }
  };

  if (!enabled || !activeAnnouncement) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <Dialog
        open={!!activeAnnouncement}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '85vh',
          }
        }}
      >
        <DialogTitle>{activeAnnouncement.title}</DialogTitle>
        <DialogContent>
          <AnnouncementContent announcement={activeAnnouncement} />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleClose}
          >
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
